<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";

import { navigationActions } from "@/action/navigationActions";
import { toggleActions } from "@/action/toggleActions";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import type { BoardCard } from "@/model/card";
import type { Objective } from "@/model/objective";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useLinkStore } from "@/store/link";
import { stopScrollPropagation } from "@/utils/dom/dom";
import { adjustScrollbarWidth } from "@/utils/dom/scrollbarWidth";

import TeamObjectiveRow from "./TeamObjectiveRow.vue";

const { registerShortcut } = useShortcuts();
registerShortcut(toggleActions.showTeamObjectives);

const rootRef = useTemplateRef("root");
const selectedObjectiveId = ref("");

onMounted(() => adjustScrollbarWidth(rootRef.value!));

const board = computed(() => useBoardsStore().boardByType("team"));
const masterCards = computed<Array<BoardCard | null>>(() => {
  const cards: Array<BoardCard | null> = [useBoardStore().activeCard];
  const pinnedCards = useLinkStore().markingCardsLinkedCards;
  if (pinnedCards) {
    cards.push(...pinnedCards);
  }
  return cards;
});

const highlightedObjectives = computed(() =>
  masterCards.value.flatMap((card) => card?.data.objectives?.map((obj) => obj.id) || []),
);

function isSelected(objectiveId: Objective["id"]) {
  return (
    selectedObjectiveId.value === objectiveId || highlightedObjectives.value.includes(objectiveId)
  );
}

function editObjectives() {
  navigationActions.openModal("board", "TeamObjectivesModal", {
    attrs: { boardId: board.value.id },
  });
}

function toggleObjective(objectiveId: Objective["id"]) {
  if (selectedObjectiveId.value === objectiveId) {
    removeObjective();
  } else {
    selectedObjectiveId.value = objectiveId;
    const linkStore = useLinkStore();
    linkStore.markObjectiveLinkedCards(objectiveId);
  }
}

function blurObjective(event: FocusEvent) {
  if ((event.relatedTarget as Element)?.classList.contains("objective")) {
    return;
  }
  removeObjective();
}

function removeObjective() {
  if (selectedObjectiveId.value !== "") {
    useLinkStore().removeObjectiveLinkedMarks(selectedObjectiveId.value);
    selectedObjectiveId.value = "";
  }
}
</script>

<!-- eslint-disable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
<template>
  <div ref="root" class="team-objectives" @keydown.esc="removeObjective">
    <div class="header" @click="editObjectives">
      <!-- eslint-enable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
      <h2 class="header-title h3">
        <SvgIcon
          name="board/objectives-team"
          class="team-objectives-icon"
          width="0.9em"
          height="0.9em"
          aria-hidden="true"
        />
        <span>{{ $t("teamObjectives.teamObjectives") }}</span>
      </h2>
      <IconButton
        id="team-objectives-trigger"
        icon="arrow/expand"
        size="small"
        scale="board"
        :text="$t('teamObjectives.expand')"
        :aria-label="$t('label.teamObjectives.expand')"
      />
    </div>
    <div class="scrollable" @wheel="stopScrollPropagation">
      <div>
        <div class="objective-group-header">
          <h3 class="h4">{{ $t("objectives.committed") }}</h3>
          <span aria-hidden="true">
            <div class="objective-number">
              {{ board.objectives.length }}
            </div>
          </span>
        </div>
        <MenuList selectable>
          <MenuListItems :label="$t('objectives.committed')" no-scroll-on-hover>
            <TeamObjectiveRow
              v-for="(objective, index) in board.objectives"
              :key="objective.id"
              :index="index + 1"
              :objective
              :board
              :selected="isSelected(objective.id)"
              class="objective"
              @click="toggleObjective(objective.id)"
              @blur="blurObjective"
            />
          </MenuListItems>
        </MenuList>
        <div />
        <div class="objective-group-header">
          <span>
            <h3 class="h4">{{ $t("objectives.uncommitted") }}</h3>
          </span>
          <span aria-hidden="true">
            <div class="objective-number">
              {{ board.stretchObjectives.length }}
            </div>
          </span>
        </div>
        <MenuList selectable>
          <MenuListItems :label="$t('objectives.uncommitted')" no-scroll-on-hover>
            <TeamObjectiveRow
              v-for="(stretchObjective, index) in board.stretchObjectives"
              :key="stretchObjective.id"
              :index="index + 1"
              :objective="stretchObjective"
              :board
              :selected="isSelected(stretchObjective.id)"
              class="objective"
              @click="toggleObjective(stretchObjective.id)"
              @blur="blurObjective"
            />
          </MenuListItems>
        </MenuList>
        <div />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/board";
@use "@/styles/font";
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.team-objectives {
  .header {
    :deep(.base-button.ghost) {
      padding: 0;
      height: board.len(32px);
      width: board.len(32px);
      border-radius: board.len(4px);

      svg {
        width: board.len(18px);
        height: board.len(18px);
      }
    }
  }

  .header-title {
    display: inline-flex;
    align-items: center;
    gap: board.len(8px);

    .team-objectives-icon {
      color: colors.$icon-subtle;
    }

    span {
      @include typography.medium;

      // approximately size as the iteration name in TeamIteration.vue
      font-size: board.len(19px);
    }
  }
}
</style>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/board";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";

.board-team {
  .team-objectives {
    @include board.board-control;

    position: relative;
    padding: 0;
    display: flex;
    flex-direction: column;

    .objective-group-header {
      padding: board.len(12px) board.len(24px);
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .objective-number {
        color: colors.$text-subtle;
        font-size: board.len(14px);
        font-weight: font.$weight-semi-bold;
      }

      .link {
        margin-right: board.len(38px);
      }
    }

    .header {
      width: 100%;
      color: colors.$text;
      background-color: colors.$background-ui;
      height: board.len(52px);
      padding: 0 board.len(20px);
      padding-right: board.len(12px);
      border-bottom: board.len(1px) solid colors-a11y.$board-border;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;

      .base-button {
        position: relative;
        top: board.len(-2px);
        font-size: inherit;
        padding: board.len(12px);

        svg {
          width: board.len(20px);
          height: board.len(20px);
        }
      }
    }

    .menu-list-items {
      gap: board.len(8px);
    }

    .scrollable {
      position: relative;
      padding-bottom: board.len(12px);
    }
  }
}
</style>
